import * as config from './config';
import BaseFactory from './BaseFactory';
import MemoryCache from './MemoryCache';
import logging from './logging';
import { Color, CubeRefractionMapping, CubeTextureLoader, Material, MeshBasicMaterial, MeshPhongMaterial, MeshStandardMaterial, MixOperation, NoColorSpace, Texture, TextureLoader, RepeatWrapping, ColorRepresentation, SRGBColorSpace, LoadingManager, DefaultLoadingManager, MeshPhysicalMaterial, } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { Material as MaterialModel } from './domain';
import { gammaCorrect as gc } from './utils/gammaCorrect';
import assets from './assets';
import traverse from './utils/traverseGenerator';
import Signal from './Signal';

const log = logging('MaterialFactory');

export const blank = new MeshBasicMaterial(
    {
        colorWrite: false,
        depthTest: false,
        depthWrite: false,
        stencilWrite: false,
        visible: false,
    });

export class MaterialFactory extends BaseFactory {

    public manager: LoadingManager;
    public textureCube: Texture;

    private textures: MemoryCache<Texture>;
    private materials: MemoryCache<Material>;

    private gltfLoader: GLTFLoader = new GLTFLoader();

    // Handler takes two arguments: the material that was just updated, and the
    // material in the GLB file from which it was derived.
    private onMaterialUpdate = new Signal<[updated: Material, source: Material]>(
        'onMaterialUpdate', 'MaterialFactory');

    // Public Interface
    // ----------------

    // **new MaterialFactory()**
    //
    // The `MaterialFactory` is designed for a single page global instance. If
    // there are multilple scenes and canvases on a page, they should still be
    // able to share the same global instance. Throughout the examples I've used
    // the variable `mfac` to represent the global instance, but there's nothing
    // special about that name.
    constructor(manager?: LoadingManager) {
        super();
        this.manager = manager ? manager : DefaultLoadingManager;
        this.textures = new MemoryCache();
        this.materials = new MemoryCache();

        // We're using a global texture cube in the first iteration, so this
        // will move to a more formal mechanism later.
        this._initTextureCube();
    }


    // **make(name, ...args)**
    //
    // This method provides a single point of entry into the material factory.
    // If you want a chrome material, you can call `mfac.make('Chrome')`, or a
    // wood grain would look like `mfac.make('StandardMelamine',
    // 'maps/001.png')`.
    make(name: string, ...args: any[]) {
        this.manager.itemStart(name);
        try {
            let material = this.materials.get(name, ...args);
            if (material == null) {
                material = super.make(name, ...args);

                material.name = name;
                this.materials.put(material, name, ...args);
            }
            return material;
        } catch(error) {
            log.error(error);
        }finally {
            this.manager.itemEnd(name);
        }
    }

    // Makes a basic material from a plain JavaScript object with material and
    // color properties.
    // TODO: Eventually the make method should be fully aware of domain models.
    makefrom(model: MaterialModel) {
        // If we have an assetId, then this is loaded from a GLB file. We
        // return a MeshStandardMaterial and then load it from the GLB file.
        if(model.assetId) {
            let mat = this.materials.get(model.assetId) as MeshStandardMaterial;
            if(mat) return mat;
            // The three.js default is currently a plain white diffuse
            // material. This will be the appearance until the glb file
            // finishes loading.
            mat = new MeshStandardMaterial();
            mat.name = model.assetId;
            this.materials.put(mat, model.assetId);

            this.gltfLoader.load(
                assets.resolve(model.assetId, 'OnlineMaterial'),
                gltf => {
                    for(const obj of traverse(gltf.scene)) {
                        if(obj?.isMesh) {
                            // If the GLB file has a MeshPhysicalMaterial,
                            // then we need to update `mat` to be an instance
                            // of that class.
                            if(obj.material instanceof MeshPhysicalMaterial) {
                                Object.assign(mat, obj.material);
                                Object.setPrototypeOf(mat, MeshPhysicalMaterial.prototype);
                            }
                            mat.copy(obj.material);
                            mat.envMap = this.textureCube;
                            this.onMaterialUpdate.trigger(mat, obj.material);
                            break;
                        }
                    }
                    // because mat was cached even before loading
                    // completed, every mesh using the assetId should get
                    // the same material. So this should update all the
                    // meshes using mat at once.
                    mat.needsUpdate = true;
                });

            return mat;
        }
        switch (model.type) {
            case 'WindowEnvironment':
                // the extra color parameter would mess up the WindowEnvironment, so we omit it
                return this.make(model.type);
            default:
                return this.make(model.type, model.color);

        }
    }

    makeTransparent(model: MaterialModel, opacity: number) {
        const original = this.makefrom(model);

        let result = original;
        if(opacity < 1) {
            const cached = this.materials.get(
                "transparent", model.assetId, model.type, model.color,
                String(opacity));
            if(cached) {
                return cached;
            }

            result = original.clone();
            result.transparent = true;
            result.opacity = opacity;

            const handler = (updated: Material, source: Material) => {
                if(updated == original) {
                    if(source instanceof MeshPhysicalMaterial) {
                        Object.assign(result, source);
                        Object.setPrototypeOf(result, MeshPhysicalMaterial.prototype);
                    }
                    result.copy(source);
                    result.transparent = true;
                    result.opacity = opacity;
                    (result as MeshStandardMaterial).envMap = this.textureCube;
                    result.needsUpdate = true;

                    this.onMaterialUpdate.delete(handler);
                }
            };
            this.onMaterialUpdate.add(handler);

            this.materials.put(
                result, "transparent", model.assetId, model.type, model.color,
                String(opacity));
        }

        return result;
    }

    // **preloadTexture**
    //
    // This is a 'lazy loader' for texture maps. If you know all the texture
    // maps you're going to use on a page, you can call this to preload them
    // during page load so that the global DefaultLoadingManager can track them.
    // In any event this simply caches a texture by name, irrespective of
    // quality level, and returns that texture instance instead of double
    // loading.
    preloadTexture(texName: string) {
        let tex = this.textures.get(texName);
        if (tex == null) {
            const loader = new TextureLoader();
            tex = loader.load(
                texName,
                () => { /* noop onLoad */ },
                null, /* progress events no longer supported */
                (err) => {
                    console.trace(
                        `An error occurred while loading '${texName}'`, err);
                });
            tex.wrapS = tex.wrapT = RepeatWrapping;
            tex.colorSpace = SRGBColorSpace;
            this.textures.put(tex, texName);
        }
        return tex;
    }

    // **_initTextureCube()**
    //
    // For now, I think we can use the same texture cube for every reflective
    // material. When we add higher quality levels, we'll probably want to
    // load separate texture cubes for different materials.
    _initTextureCube() {
        if (this.textureCube == null) {
            this.textureCube = this.loadTextureCube(
                `${config.maps}/schadoplatz/32p/`, 'jpg');
        }
    }

    loadTextureCube(path: string, suffix: string) {
        let cube = this.textures.get(path, suffix);
        if(cube == null) {
            cube = new CubeTextureLoader()
                .setPath(path)
                .load([`px.${suffix}`, `nx.${suffix}`, `py.${suffix}`, `ny.${suffix}`, `pz.${suffix}`, `nz.${suffix}`]);
            this.textures.put(cube, path, suffix)
        }
	cube.colorSpace = NoColorSpace;
        return cube;
    }
}

const mfac = MaterialFactory.getInstance() as MaterialFactory;

// Material Definitions
// --------------------

// Everything below here is a material definition. 

mfac.extend('LightWarm', function() {
    var material = new MeshBasicMaterial({
        color: gc(0xe0d2ba),
    });
    return material;
});

mfac.extend('LightNeutral', function() {
    var material = new MeshBasicMaterial({
        color: gc(0xd6d4d6),
    });
    return material;
});

mfac.extend('LightCool', function() {
    var material = new MeshBasicMaterial({
        color: gc(0xd3d4dc),
    });
    return material;
});


mfac.extend('Chrome', function() {
    var material = new MeshPhongMaterial({
        color: 0xFFFFFF,
        specular: 0xFFFFFF,
        shininess: 100,
        reflectivity: 0.95,
        combine: MixOperation,
        envMap: this.textureCube
    });

    return material;
});

mfac.extend('Brass', function() {
    var material = new MeshPhongMaterial({
        // This is the exact material color we use in Cycles.
        color: gc(0xE4C28C),
        // The material color at 40% value
        specular: gc(0x66573f),
        shininess: 100,
        envMap: this.textureCube
    });
    return material;
});

mfac.extend('SatinNickel', function() {
    var material = new MeshPhongMaterial({
        color: gc(0xD0D0D8),
        specular: gc(0x111116),
        shininess: 50,
        reflectivity: 0.18,
        combine: MixOperation,
        envMap: this.textureCube
    });
    return material;
});

mfac.extend('Nickel', function() {
    return this.make('SatinNickel');
});

mfac.extend('Bronze', function() {
    var material = new MeshPhongMaterial({
        // Again, this value comes from Cycles material settings
        color: gc(0x0c0804),
        //  specular: 0x543505,
        shininess: 1,
        reflectivity: 0.05,
        combine: MixOperation,
        envMap: this.textureCube
    });

    return material;
});

mfac.extend('MatteGold', function() {
    var material = new MeshPhongMaterial({
        // Again, this value comes from Cycles material settings
        color: gc(0xC6A677),
        // The material hue at 25% luminosity
        specular: gc(0x403526),
        shininess: 50,
        reflectivity: 0.18,
        combine: MixOperation,
        envMap: this.textureCube
    });
    return material;
});

mfac.extend('GlossBlack', function() {
    var material = new MeshPhongMaterial({
        color: gc(0x101010),
        specular: gc(0x222222),
        shininess: 100,
        envMap: this.textureCube
    });
    return material;
});

mfac.extend('MatteBlack', function() {
    return new MeshStandardMaterial({
        color: 0x282828,
        roughness: 50,
    });
});

mfac.extend('MatteAluminum', function() {
    var material = new MeshPhongMaterial({
        color: gc(0xA0A0A0),
        specular: gc(0x282828),
        shininess: 20,
        reflectivity: 0.5,
        combine: MixOperation,
        envMap: this.textureCube
    });
    return material;
});

// This has never been used, and it doesn't work anyway
//mfac.extend('MatteNickel', function() {
//    var material = new MeshPhongMaterial({
//        color: gc(0xD9D2C5),
//        specular: gc(0x3674B1),
//        shininess: 20,
//        reflectivity: 0.05,
//        combine: MixOperation,
//        envMap: this.textureCube
//    });
//    return material;
//});

mfac.extend('Slate', function() {
    var material = new MeshPhongMaterial({
        color: gc(0x303030),
        specular: gc(0x323232),
        shininess: 100,
        envMap: this.textureCube
    });
    return material;
});

mfac.extend('White', function() {
    var material = new MeshPhongMaterial({
        color: 0xffffff,
        specular: 0xffffff,
        reflectivity: 0.5,
        shininess: 100,
        envMap: this.textureCube
    });
    return material;
});

mfac.extend('Ivory', function() {
    var material = new MeshPhongMaterial({
        color: gc(0xffffd0),
        specular: gc(0xfffff0),
        shininess: 100,
        reflectivity: 0.5,
        envMap: this.textureCube
    });
    return material;
});

mfac.extend('Mirror', function() {
    return this.make('Chrome');
});

mfac.extend('Glass', function() {
    var material = new MeshPhongMaterial({
        color: 0xFFFFFF,
        specular: 0xFFFFFF,
        shininess: 100,
        envMap: this.textureCube,
        transparent: true,
        opacity: 0.2,
    });
    return material;
});

mfac.extend('FrostedGlass', function() {
    var material = new MeshPhongMaterial({
        color: 0xFFFFFF,
        specular: 0xFFFFFF,
        shininess: 100,
        reflectivity: 0.1,
        combine: MixOperation,
        envMap: this.textureCube,
        transparent: true,
        opacity: 0.6,
    });
    return material;
});

mfac.extend('StandardMelamine', function(texName) {
    var tex = this.preloadTexture(texName);
    var material = new MeshPhongMaterial({
        map: tex,
        shininess: 30
    });
    material.userData.material_colormap = texName;
    return material;
});

mfac.extend('SemiGlossMelamine', function(texName) {
    var tex = this.preloadTexture(texName);
    var material = new MeshPhongMaterial({
        map: tex,
        shininess: 90
    });
    material.userData.material_colormap = texName;
    return material;
});

mfac.extend('GlossMelamine', function(texName: string, tint: number=0xFFFFFF) {
    var tex = this.preloadTexture(texName);
    var material = new MeshPhongMaterial({
        map: tex,
        shininess: 100,
        envMap: this.textureCube,
        reflectivity: 0.05,
        combine: MixOperation,
        color: tint,
    });
    material.userData.material_colormap = texName;
    material.userData.material_tint = tint;
    return material;
});

mfac.extend('Carpet', function(colorCode: string) {
    var color = this.preloadTexture(
        `${config.maps}/flooring/carpet_diffuse_01.jpg`);
    var normal = this.preloadTexture(
        `${config.maps}/flooring/carpet_normal.jpg`);

    var material = new MeshPhongMaterial({
        color: new Color(colorCode), //MJB
        map: color,
        shininess: 4,
        normalMap: normal
    });
    material.userData.material_tint = colorCode;
    return material;
});

// Note that the `colorCode` is a string, either '01', '02', '03', '04', '05',
// '06', '07', '08', '09', or '10'.
mfac.extend('OriginalFlooring', function(colorCode) {
    var color = this.preloadTexture(
        `${config.maps}/flooring/Floor_Original_diffuse_${colorCode}.jpg`);
    var normal = this.preloadTexture(
        `${config.maps}/flooring/Floor_Original_normal.jpg`);
    var specular = this.preloadTexture(
        `${config.maps}/flooring/Floor_Original_specular.jpg`);
    var material = new MeshPhongMaterial({
        //color: gc(0x808080),
        map: color,
        shininess: 70,
        specularMap: specular,
        normalMap: normal,
        envMap: this.textureCube,
        reflectivity: 0.6,
        combine: MixOperation
    });
    material.userData.material_tint = colorCode;
    return material;
});

mfac.extend('Parquet', function(map: string) {
    var material = null;
    if (!map) {
        map = "01A";
    }
    var mapCategory = map.slice(0, -1);
    var color = this.preloadTexture(
        `${config.maps}/flooring/Floor_Parquet_${map}.jpg`);
    var rough = null;
    if (this.level >= 4) {
        var rough = this.preloadTexture(
            `${config.maps}/flooring/Floor_Parquet_${mapCategory}_rough.jpg`);
    }
    material = new MeshStandardMaterial({
        map: color,
        roughnessMap: rough,
        // Setting the map and its intensity to zero effectively cancels the
        // influence of the scene.environment map
        envMap: this.textureCube,
        envMapIntensity: 0,
    });
    material.userData.material_colormap = map;
    return material;
});

mfac.extend('Herringbone', function(map: string) {
    var material = null;
    if (!map)
        map == "01A";
    var mapCategory = map.slice(0, -1);
    var color = this.preloadTexture(
        `${config.maps}/flooring/Floor_Herringbone_${map}.jpg`);
    var normal = this.preloadTexture(
        `${config.maps}/flooring/Floor_Herringbone_${mapCategory}_normal.jpg`);
    var height = this.preloadTexture(
        `${config.maps}/flooring/Floor_Herringbone_${mapCategory}_height.jpg`);
    var rough = this.preloadTexture(
        `${config.maps}/flooring/Floor_Herringbone_${mapCategory}_rough.jpg`);

    material = new MeshStandardMaterial({
        map: color,
        normalMap: normal,
        bumpMap: height,
        roughnessMap: rough,
        // Setting the map and its intensity to zero effectively cancels the
        // influence of the scene.environment map
        envMap: this.textureCube,
        envMapIntensity: 0,
    });
    material.userData.material_colormap = map;
    return material;
});

mfac.extend('WindowEnvironment', function(path, suffix) {

    if (path == null) path = `${config.maps}/we_original/`;
    if (suffix == null) suffix = 'jpg';

    let env = this.loadTextureCube(path, suffix);
    env.mapping = CubeRefractionMapping;

    let material = new MeshPhongMaterial({
        color: 0xFFFFFF,
        shininess: 100,
        envMap: env,
        refractionRatio: 0.98,
        combine: MixOperation
    });
    return material;
});

mfac.extend('GenericAsset', function(map) {
    var color = this.preloadTexture(map);
    const material = new MeshPhongMaterial({
        color: 0xFFFFFF,
        map: color,
    });
    return material;
});

mfac.extend('LeatherWinter', function() {
    var color = this.preloadTexture(
        `${config.maps}/leathers/Leather_Winter.jpg`);
    var normal = this.preloadTexture(
        `${config.maps}/leathers/Leather_Normal.jpg`);
    var rough = this.preloadTexture(
        `${config.maps}/leathers/Leather_Rough.jpg`);
    let material = new MeshStandardMaterial({
        map: color,
        roughnessMap: rough,
        roughness: 1.0,
        normalMap: normal,
        metalness: 0,
        metalnessMap: rough,
        //refractionRatio: 1.5,
        //combine: MixOperation
    });
    return material;
});

mfac.extend('LeatherOyster', function() {
    var color = this.preloadTexture(
        `${config.maps}/leathers/Leather_Oyster.jpg`);
    var normal = this.preloadTexture(
        `${config.maps}/leathers/Leather_Normal.jpg`);
    var rough = this.preloadTexture(
        `${config.maps}/leathers/Leather_Rough.jpg`);
    let material = new MeshStandardMaterial({
        map: color,
        roughnessMap: rough,
        roughness: 1.0,
        normalMap: normal,
        metalness: 0,
        metalnessMap: rough,
        //refractionRatio: 1.5,
        //combine: MixOperation
    });
    return material;
});

mfac.extend('LeatherPewter', function() {
    var color = this.preloadTexture(
        `${config.maps}/leathers/Leather_Pewter.jpg`);
    var normal = this.preloadTexture(
        `${config.maps}/leathers/Leather_Normal.jpg`);
    var rough = this.preloadTexture(
        `${config.maps}/leathers/Leather_Rough.jpg`);
    let material = new MeshStandardMaterial({
        map: color,
        roughnessMap: rough,
        roughness: 1.0,
        normalMap: normal,
        metalness: 0,
        metalnessMap: rough,
        //refractionRatio: 1.5,
        //combine: MixOperation
    });
    return material;
});

mfac.extend('WallPaint', function(wallColor: ColorRepresentation) {
    if(wallColor == null) wallColor = 0xB0B0B0;
    const tex = this.preloadTexture("/closet-organizers-images/3D Assets/wall1.png");
    tex.repeat.set(0.02, 0.02);
    return new MeshStandardMaterial({
        color: gc(wallColor),
        map: tex,
        roughness: 1.0,
        metalness: 0.0,
    });
});

mfac.extend('WallExtension', function(wallColor: ColorRepresentation) {
    if(wallColor == null) wallColor = 0xB0B0B0;
    const tex = this.preloadTexture("/closet-organizers-images/3D Assets/wall_extension.png");
    tex.repeat.set(0.02, 0.02);
    return new MeshStandardMaterial({
        color: gc(wallColor),
        map: tex,
        roughness: 1.0,
        metalness: 0.0,
    });
});


export default mfac;
